// dashboard/src/components/Interact/MediaWindow.tsx
import React, { useRef } from "react";

// Stable-size box for video/camera/image display. Media of any aspect ratio
// is scaled to fit inside without cropping (letterboxed/pillarboxed), so
// loading different media never reflows the surrounding layout.
//
// The window's own aspect ratio responds to viewport orientation (portrait
// vs landscape) via a CSS media query, not device type or JS resize
// listeners: on this app the left nav collapses when a practice session
// starts, so the browser viewport reliably fills the majority of the
// screen while a session is active.
//
// IMPLEMENTATION NOTE: this deliberately does NOT use the CSS `aspect-ratio`
// property. On at least one iOS Safari version, a <video> sized via
// `aspect-ratio` on its container + `object-fit: contain` on itself has been
// observed to play back visually rotated 90°, while the same video plays
// back correctly when the container is sized the old-fashioned way (a
// `padding-top: X%` box). That's a narrower, more suspicious difference than
// it looks: aspect-ratio-driven sizing and object-fit intrinsic-size
// computation are a newer code path in these engines than the padding-top
// technique, which has been reliable across browsers for over a decade. If
// this fix doesn't hold, that theory is wrong and the bug is elsewhere — but
// it's a cheap, safe thing to rule out first.

// React 16 here has no useId(); a module-level counter gives each instance
// a unique class name for the scoped <style> block instead.
let mediaWindowIdCounter = 0;

/** "16/9" -> 56.25 (the padding-top percentage that yields that ratio's height for a given width) */
const aspectRatioToPaddingPercent = (ratio: string): number => {
  const [wStr, hStr] = ratio.split("/").map((s) => s.trim());
  const w = parseFloat(wStr);
  const h = parseFloat(hStr);
  if (!w || !h) {
    // eslint-disable-next-line no-console
    console.error(`MediaWindow: could not parse aspect ratio "${ratio}", falling back to 16/9`);
    return (9 / 16) * 100;
  }
  return (h / w) * 100;
};

interface MediaWindowProps {
  portraitAspectRatio?: string;
  landscapeAspectRatio?: string;
  maxWidth?: string;
  backgroundColor: string;
  borderRadius?: string;
  children: React.ReactNode;
}

const MediaWindow = ({
  portraitAspectRatio = "3/4",
  landscapeAspectRatio = "16/9",
  maxWidth = "100%",
  backgroundColor,
  borderRadius,
  children,
}: MediaWindowProps) => {
  const idRef = useRef<string>();
  if (!idRef.current) {
    idRef.current = `media-window-${++mediaWindowIdCounter}`;
  }
  const className = idRef.current;

  const landscapePct = aspectRatioToPaddingPercent(landscapeAspectRatio);
  const portraitPct = aspectRatioToPaddingPercent(portraitAspectRatio);

  return (
    <div
      className={className}
      style={{
        width: "100%",
        maxWidth,
        margin: "0 auto",
        position: "relative",
        // height comes entirely from padding-top below, not from this.
        height: 0,
        overflow: "hidden",
        backgroundColor,
        borderRadius,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        {children}
      </div>
      <style>{`
        .${className} {
          padding-top: ${landscapePct}%;
        }
        @media (orientation: portrait) {
          .${className} {
            padding-top: ${portraitPct}%;
          }
        }
      `}</style>
    </div>
  );
};

export default MediaWindow;